import App from "@bloglovin/inspire-engine/App";
import { contextualAppName, isCompiledRenderSurface } from "%/dynamic-component-editor/classes/platform-standard-entities";
import { useStoryStore } from "%/dynamic-component-editor/stores/story-store";
import { applyStoryFields } from "%/dynamic-component-editor/standard-components/base-story-card/member-post-feed-actions";
import {
  SHARE_BY_SHEET,
  VOTE_DOWN,
  VOTE_NONE,
  VOTE_UP,
  buildConfirmedEngagementFields,
  createEngagementQueue,
  engagesThroughStoryEngagement,
  isLiked,
  shareRouteFor,
} from "%/dynamic-component-editor/helpers/story-engagement.mjs";
import { KIND_LIKE, STORY_ENGAGEMENT_APP } from "%/dynamic-component-editor/constants/story-engagement-constants.mjs";

const STORY_ENTITY_TYPE = "Story";
const VOTE_ACTIONS = {
  [VOTE_UP]: "upvote",
  [VOTE_DOWN]: "downvote",
  [VOTE_NONE]: "remove-vote",
};
const SHARE_CONFIRMATION_MS = 2000;
const MSG_ENGAGEMENT_FAILED = "A like or vote on a story did not save; the button was put back to what the server last confirmed";
const MSG_SHARE_NOT_COUNTED = "A story was shared but the share was not counted";

export { VOTE_DOWN, VOTE_NONE, VOTE_UP, SHARE_CONFIRMATION_MS, isLiked };

export function engagementFieldsFromStoryEntity(entity) {
  return {
    likeStatus: isLiked(entity.likeStatus),
    likeCount: Number(entity.likeCount ?? 0),
    voteStatus: Number(entity.voteStatus ?? VOTE_NONE),
    voteScore: Number(entity.voteScore ?? 0),
    upvoteCount: Number(entity.upvoteCount ?? 0),
    downvoteCount: Number(entity.downvoteCount ?? 0),
  };
}

export function engagementFieldsFromStoryPageRecord(record) {
  return {
    likeStatus: isLiked(record.likeStatus),
    likeCount: Number(record.numLikes ?? 0),
    voteStatus: Number(record.voteStatus ?? VOTE_NONE),
    voteScore: Number(record.voteScore ?? 0),
    upvoteCount: Number(record.upvoteScore ?? 0),
    downvoteCount: Number(record.downvoteScore ?? 0),
  };
}

function showEngagement(storyId, fields) {
  const app = App.get(contextualAppName());
  if (app.hasEntity(STORY_ENTITY_TYPE, { id: Number(storyId) })) {
    applyStoryFields(storyId, {
      likeStatus: fields.likeStatus,
      likeCount: fields.likeCount,
      voteStatus: fields.voteStatus,
      voteScore: fields.voteScore,
      upvoteCount: fields.upvoteCount,
      downvoteCount: fields.downvoteCount,
    });
  }
  const pageRecord = useStoryStore().stories?.[storyId];
  if (pageRecord) {
    Object.assign(pageRecord, {
      likeStatus: fields.likeStatus,
      numLikes: fields.likeCount,
      voteStatus: fields.voteStatus,
      voteScore: fields.voteScore,
      upvoteScore: fields.upvoteCount,
      downvoteScore: fields.downvoteCount,
    });
  }
}

function likeActionFor(liked) {
  return liked ? "like" : "unlike";
}

function findStoryPublicationId(storyId) {
  const app = App.get(contextualAppName());
  const pk = { id: Number(storyId) };
  const publication = app.hasEntity(STORY_ENTITY_TYPE, pk) ? app.getEntityField(STORY_ENTITY_TYPE, pk, "publication") : null;
  if (publication) {
    return Number(publication.pk.id);
  }
  return Number(useStoryStore().stories[storyId]?.publishingPlatformId ?? 0);
}

function fetchStoryPublicationId(storyId) {
  const knownPublicationId = findStoryPublicationId(storyId);
  if (knownPublicationId > 0) {
    return Promise.resolve(knownPublicationId);
  }
  return useStoryStore().getOrFetchStory(storyId).then(record => Number(record?.publishingPlatformId ?? 0));
}

function engagesNativelyFor(storyPublicationId) {
  const storyStore = useStoryStore();
  return engagesThroughStoryEngagement({
    renderSurface: isCompiledRenderSurface(),
    nativeOn: storyStore.isStoryEngagementOn,
    appInScope: App.get(contextualAppName()).isAppNameInScope(STORY_ENGAGEMENT_APP),
    destinationId: storyStore.renderDestinationId,
    storyPublicationId,
  });
}

export function engagesNatively(storyId) {
  return Promise.resolve(storyId).then(fetchStoryPublicationId).then(engagesNativelyFor);
}

function readShownEngagement(storyId) {
  const app = App.get(contextualAppName());
  const pk = { id: Number(storyId) };
  if (app.hasEntity(STORY_ENTITY_TYPE, pk)) {
    return engagementFieldsFromStoryEntity(app.getEntity(STORY_ENTITY_TYPE, pk));
  }
  return engagementFieldsFromStoryPageRecord(useStoryStore().stories[storyId] ?? {});
}

function sendEngagementEvent(kind, storyId, target, onDone, onError) {
  useStoryStore().sendStoryEngagementEvent(storyId, kind, target, threadCounts => {
    onDone(buildConfirmedEngagementFields(kind, target, readShownEngagement(storyId), threadCounts));
  }, onError);
}

function sendEngagementRequest(kind, storyId, target, onDone, onError) {
  const action = kind === KIND_LIKE ? likeActionFor(target) : VOTE_ACTIONS[target];
  useStoryStore().sendStoryEngagement(storyId, action, onDone, onError);
}

function sendEngagement(kind, storyId, target, onDone, onError) {
  engagesNatively(storyId).then(native => {
    const send = native ? sendEngagementEvent : sendEngagementRequest;
    send(kind, storyId, target, onDone, onError);
  }).catch(onError);
}

function reportEngagementFailure(error, context) {
  // eslint-disable-next-line no-console
  console.error(MSG_ENGAGEMENT_FAILED, { ...context, error: error?.response?.data ?? error?.message ?? error });
}

const engagementQueue = createEngagementQueue({
  send: sendEngagement,
  apply: showEngagement,
  report: reportEngagementFailure,
});

function readerCanEngage() {
  const storyStore = useStoryStore();
  if (storyStore.isUserLoggedIn) {
    return true;
  }
  storyStore.redirectToLoginToEngage();
  return false;
}

export function tapStoryLike(storyId, fields) {
  if (readerCanEngage()) {
    engagementQueue.tapLike(Number(storyId), fields);
  }
}

export function tapStoryVote(storyId, fields, direction) {
  if (readerCanEngage()) {
    engagementQueue.tapVote(Number(storyId), fields, direction);
  }
}

function showShareCount(storyId, shareCount) {
  const app = App.get(contextualAppName());
  if (app.hasEntity(STORY_ENTITY_TYPE, { id: Number(storyId) })) {
    applyStoryFields(storyId, { numShares: shareCount });
  }
  const pageRecord = useStoryStore().stories?.[storyId];
  if (pageRecord) {
    pageRecord.shareCount = shareCount;
  }
}

function countShare(storyId) {
  if (!useStoryStore().isUserLoggedIn) {
    return;
  }
  useStoryStore().recordStoryShare(
    storyId,
    shareCount => showShareCount(storyId, shareCount),
    error => {
      // eslint-disable-next-line no-console
      console.error(MSG_SHARE_NOT_COUNTED, { storyId, error: error?.message ?? error });
    },
  );
}

export function shareStory(storyId, { title, url }, onShared) {
  const id = Number(storyId);
  const route = shareRouteFor({
    hasShareSheet: typeof navigator.share === "function",
    touchFirst: Boolean(window.matchMedia?.("(pointer: coarse)").matches),
  });
  if (route === SHARE_BY_SHEET) {
    navigator.share({ title, url }).then(
      () => {
        countShare(id);
        onShared("shared");
      },
      error => {
        if (error?.name !== "AbortError") {
          // eslint-disable-next-line no-console
          console.error("The browser's share sheet failed to open for a story", { storyId: id, error: error?.message });
        }
      },
    );
    return;
  }
  navigator.clipboard.writeText(url).then(
    () => {
      countShare(id);
      onShared("copied");
    },
    error => {
      // eslint-disable-next-line no-console
      console.error("Copying a story's link for sharing failed", { storyId: id, url, error: error?.message });
    },
  );
}
